import React, { useState, useEffect } from "react";
import { Save, Download } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_NODE_API_BASE_URL || process.env.REACT_APP_API_BASE_URL;

const Drafts = () => {
  const [templateTypes, setTemplateTypes] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Fetch template types (names and URLs)
  useEffect(() => {
    const fetchTemplateTypes = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/drafts/getAllTemplateNames`);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        setTemplateTypes(data);
      } catch (error) {
        console.error("Error fetching templates:", error);
        setError("Error fetching template types. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplateTypes();
  }, []);

  // Fetch the content of the selected template
  const fetchTemplateContent = async (templateUrl) => {
    setSelectedTemplate(templateUrl);
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/drafts/getTemplate?url=${encodeURIComponent(templateUrl)}`
      );
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const text = await response.text();
      setContent(text);
    } catch (error) {
      console.error("Error fetching template content:", error);
      setError("Error fetching template content. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Function to download the edited file in the specified format (PDF or DOCX)
  const downloadEditedFile = async (fileType) => {
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/drafts/convertFile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          content: content,
          fileType: fileType,
          fileName: selectedTemplate
            .split("/")
            .pop()
            .replace(/\.[^/.]+$/, ""),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Server responded with ${response.status}: ${errorText}`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedTemplate
        .split("/")
        .pop()
        .replace(/\.[^/.]+$/, "")}.${fileType}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`File downloaded successfully as ${fileType}`);
    } catch (error) {
      console.error("Error downloading file:", error);
      setError(
        `Error downloading file as ${fileType.toUpperCase()}: ${error.message}`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const saveDraft = async () => {
    const userId = localStorage.getItem("userId"); // Assuming userId is stored in localStorage

    if (!userId) {
      setError("User ID not found. Please log in.");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/drafts/saveDraft`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userId,
          documentContent: content,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Server responded with ${response.status}: ${errorText}`
        );
      }

      console.log("Draft saved successfully");
      alert("Draft saved successfully");
    } catch (error) {
      console.error("Error saving draft:", error);
      setError(`Error saving draft: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen p-6 flex flex-col gap-4">
      <div className="bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl px-6 py-4">
        <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">Draft Editor</h1>
        <p className="text-gray-600 mt-1">Pick a template, edit content, save drafts, and export in your preferred format.</p>
      </div>
      <div className="w-full bg-gradient-to-b from-white/70 via-white/40 to-white/30 backdrop-blur-xl border border-white/40 shadow-[0_10px_25px_rgba(0,0,0,0.08)] rounded-3xl p-4 md:p-6 flex-1 min-h-0 flex flex-col">

        {/* Template Selection */}
        <div className="mb-6">
          <select
            className="w-full mr-2 px-4 py-3 rounded-xl border border-gray-300 bg-white/80 focus:outline-none focus:ring-2 focus:ring-[#FF7F50] focus:border-transparent"
            onChange={(e) => fetchTemplateContent(e.target.value)}
            value={selectedTemplate}
            disabled={isLoading}
          >
            <option value="" disabled>
              Select a Template
            </option>
            {templateTypes.map((template) => (
              <option key={template.name} value={template.url}>
                {template.name}
              </option>
            ))}
          </select>
        </div>

        {/* Content Editor */}
        {selectedTemplate && (
          <div className="flex-1 min-h-0 flex flex-col">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full flex-1 min-h-[300px] p-4 mb-4 border border-orange-200 bg-white/80 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF7F50]"
              disabled={isLoading}
              placeholder="Edit your template here..."
            />

            {/* Buttons for Saving and Downloading */}
            <div className="flex flex-wrap justify-end gap-3">
              <button
                onClick={() => saveDraft()}
                className="flex items-center bg-gradient-to-r from-orange-400 to-orange-600 text-white px-6 py-2 rounded-xl font-medium hover:from-orange-500 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-[#FF8A65] focus:ring-opacity-50"
                disabled={isLoading}
              >
                <Save className="mr-2" size={18} />
                save
              </button>
              <button
                onClick={() => downloadEditedFile("pdf")}
                className="flex items-center bg-gradient-to-r from-orange-400 to-orange-600 text-white px-6 py-2 rounded-xl font-medium hover:from-orange-500 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-[#FF8A65] focus:ring-opacity-50"
                disabled={isLoading}
              >
                <Download className="mr-2" size={18} />
                PDF
              </button>
              <button
                onClick={() => downloadEditedFile("docx")}
                className="flex items-center bg-gradient-to-r from-orange-400 to-orange-600 text-white px-6 py-2 rounded-xl font-medium hover:from-orange-500 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-[#FF8A65] focus:ring-opacity-50"
                disabled={isLoading}
              >
                <Download className="mr-2" size={18} />
                DOCX
              </button>
            </div>
          </div>
        )}

        {/* Loading and Error Handling */}
        {isLoading && <p className="mt-4 text-orange-600">Loading...</p>}
        {error && (
          <div
            className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md"
            role="alert"
          >
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Drafts;
